import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, Lock, Zap, Crown, Trophy, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getModels } from "@/lib/api";

const TIER_ICON = {
  free: Sparkles,
  standard: Zap,
  pro: Crown,
  pro_max: Trophy,
};

const PROVIDER_LABEL = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  gemini: "Google",
  sabzy: "Sabzy",
};
const PROVIDER_ORDER = ["openai", "anthropic", "gemini", "sabzy"];

const STORAGE_KEY = "sabzy_selected_model";
const FALLBACK = { label: "Gemini 3 Flash", id: "gemini-3-flash-preview", tier: "free", is_racing: false };

export function ModelPicker({ onChange, currentPlan, isAnonymous }) {
  const navigate = useNavigate();
  const [models, setModels] = useState({ available: [], all: [] });
  const [selected, setSelected] = useState(() => localStorage.getItem(STORAGE_KEY));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;
    getModels()
      .then((r) => {
        if (cancelled) return;
        setModels(r.data);
        const allowedIds = (r.data.available || []).map((m) => m.id);
        let initial = localStorage.getItem(STORAGE_KEY);
        if (!initial || !allowedIds.includes(initial)) {
          initial = r.data.default_model || allowedIds[0];
        }
        setSelected(initial);
        onChangeRef.current?.(initial);
      })
      .catch((e) => console.warn("getModels failed:", e?.message));
    return () => { cancelled = true; };
  }, [currentPlan]);

  const pick = (id, locked) => {
    if (locked) {
      navigate(isAnonymous ? "/signup" : "/upgrade");
      return;
    }
    setSelected(id);
    localStorage.setItem(STORAGE_KEY, id);
    onChange?.(id);
  };

  const currentModel =
    models.all.find((m) => m.id === selected) ||
    models.available.find((m) => m.id === models.default_model) ||
    FALLBACK;

  const CurrentIcon = TIER_ICON[currentModel.tier] || Sparkles;
  const availableIds = new Set((models.available || []).map((a) => a.id));
  const groups = PROVIDER_ORDER
    .map((p) => ({ provider: p, items: (models.all || []).filter((m) => (m.provider || "anthropic") === p) }))
    .filter((g) => g.items.length);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="group inline-flex h-9 items-center gap-2 rounded-full border border-border bg-card/60 px-3 text-xs font-medium transition hover:border-primary/40 hover:bg-card"
          data-testid="model-picker-trigger"
          aria-label="Choose model"
        >
          <CurrentIcon className="h-3.5 w-3.5 text-primary" />
          <span className="max-w-[96px] truncate sm:max-w-none">{currentModel.label}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground transition group-hover:text-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] w-80 overflow-y-auto p-1" data-testid="model-picker-menu">
        {groups.map((g, gi) => (
          <div key={g.provider}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel
              className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground"
              data-testid={`model-group-${g.provider}`}
            >
              {PROVIDER_LABEL[g.provider] || g.provider}
            </DropdownMenuLabel>
            {g.items.map((m) => {
              const Icon = TIER_ICON[m.tier] || Sparkles;
              const isActive = m.id === selected;
              const isLocked = !availableIds.has(m.id);
              return (
                <DropdownMenuItem
                  key={m.id}
                  onClick={(e) => { e.preventDefault(); pick(m.id, isLocked); }}
                  className={`group flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2 ${isActive ? "bg-primary/10" : ""}`}
                  data-testid={`model-option-${m.id}`}
                >
                  <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-md ${
                    isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  }`}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{m.label}</span>
                      {m.is_racing && (
                        <span className="rounded-sm bg-primary/15 px-1 py-px text-[8px] font-bold uppercase tracking-widest text-primary">
                          Race
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{m.sublabel}</div>
                  </div>
                  {isLocked ? (
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" data-testid={`model-locked-${m.id}`} />
                  ) : isActive ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : null}
                </DropdownMenuItem>
              );
            })}
          </div>
        ))}
        {(isAnonymous || currentPlan !== "pro_max") && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate(isAnonymous ? "/signup" : "/upgrade")}
              className="cursor-pointer rounded-md px-2.5 py-2 text-xs text-primary"
              data-testid="model-picker-upgrade"
            >
              <Crown className="mr-2 h-3.5 w-3.5" />
              {isAnonymous ? "Sign in to unlock more models" : "Upgrade to unlock more"}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
